import React from "react";
import { Box, type BoxProps } from "@chakra-ui/react";

export interface AvatarInitialsProps extends Omit<BoxProps, "ring"> {
  /** Full name; the first letters of the first two words are shown. */
  name: string;
  /** Diameter in px (default 40). */
  size?: number;
  /** 2px green ring separated from the avatar by a 2px gap in `ringGap` colour. */
  ring?: boolean;
  /** CSS colour of the ring gap — the surface behind the avatar (default navy.900 for the sidebar). */
  ringGap?: string;
}

/** "Al Falah Trading" → "AF" */
export const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  const initials = parts.map((w) => w.charAt(0).toUpperCase()).join("");
  return initials || "?";
};

/**
 * Leaf-green disc with navy initials; replaces profile_placeholder.jpeg.
 * @example <AvatarInitials name={userData.name.value} size={40} ring />
 */
export const AvatarInitials: React.FC<AvatarInitialsProps> = ({ name, size = 40, ring = false, ringGap = "var(--chakra-colors-navy-900)", ...rest }) => (
  <Box
    role="img"
    aria-label={name}
    w={`${size}px`}
    h={`${size}px`}
    minW={`${size}px`}
    borderRadius="full"
    bg="leaf.500"
    color="navy.900"
    fontFamily="body"
    fontWeight={700}
    fontSize={`${Math.round(size * 0.38)}px`}
    letterSpacing="0.02em"
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
    boxShadow={ring ? `0 0 0 2px ${ringGap}, 0 0 0 4px var(--chakra-colors-leaf-500)` : undefined}
    userSelect="none"
    {...rest}
  >
    {getInitials(name)}
  </Box>
);
